import type { EstimatedRepairCategory } from '@otw/models/expenses/expenseSettings'
import type { AppliedEstimatedRepairAddon } from '@otw/models/expenses/expenseSettings'

export const REPAIR_ADDON_MIN_QUANTITY = 1
export const REPAIR_ADDON_MAX_QUANTITY = 20

export const estimatedCategoryField = (
  category: EstimatedRepairCategory,
): 'estimated_body' | 'estimated_mechanical' | 'estimated_safety' => {
  if (category === 'body') return 'estimated_body'
  if (category === 'mechanical') return 'estimated_mechanical'
  return 'estimated_safety'
}

export const estimatedCategoryLabel = (category: EstimatedRepairCategory): string => {
  if (category === 'body') return 'Body'
  if (category === 'mechanical') return 'Mechanical'
  return 'Safety'
}

export const normalizeRepairAddonQuantity = (quantity: number | undefined | null): number => {
  const n = Number(quantity)
  if (!Number.isFinite(n)) return REPAIR_ADDON_MIN_QUANTITY
  return Math.min(
    REPAIR_ADDON_MAX_QUANTITY,
    Math.max(REPAIR_ADDON_MIN_QUANTITY, Math.floor(n)),
  )
}

export const repairAddonLineTotal = (addon: AppliedEstimatedRepairAddon): number => {
  const unit = Number(addon.amount) || 0
  return unit * normalizeRepairAddonQuantity(addon.quantity)
}

type RepairCategoryFields = {
  estimated_body?: number | null
  estimated_mechanical?: number | null
  estimated_safety?: number | null
  estimated_repair_addons?: AppliedEstimatedRepairAddon[]
}

export const adjustRepairCategoryAmount = (
  fields: RepairCategoryFields,
  category: EstimatedRepairCategory,
  delta: number,
): RepairCategoryFields => {
  const fieldName = estimatedCategoryField(category)
  const currentAmount = Number(fields[fieldName]) || 0
  return {
    ...fields,
    [fieldName]: Math.max(0, currentAmount + delta),
  }
}

export const toggleRepairAddonOnFields = (
  fields: RepairCategoryFields,
  item: Pick<AppliedEstimatedRepairAddon, 'id' | 'amount' | 'category'>,
  checked: boolean,
): RepairCategoryFields => {
  const currentAddons = fields.estimated_repair_addons ?? []
  const existing = currentAddons.find(addon => addon.id === item.id)
  const category = existing?.category ?? item.category

  if (checked) {
    if (existing) return fields
    const amount = Number(item.amount) || 0
    const quantity = REPAIR_ADDON_MIN_QUANTITY
    return adjustRepairCategoryAmount(
      {
        ...fields,
        estimated_repair_addons: [
          ...currentAddons,
          { id: item.id, amount, category, quantity },
        ],
      },
      category,
      amount * quantity,
    )
  }

  if (!existing) return fields
  const removedTotal = repairAddonLineTotal(existing)
  return adjustRepairCategoryAmount(
    {
      ...fields,
      estimated_repair_addons: currentAddons.filter(addon => addon.id !== item.id),
    },
    existing.category,
    -removedTotal,
  )
}

export const updateRepairAddonQuantityOnFields = (
  fields: RepairCategoryFields,
  addonId: string,
  quantity: number,
): RepairCategoryFields => {
  const currentAddons = fields.estimated_repair_addons ?? []
  const existing = currentAddons.find(addon => addon.id === addonId)
  if (!existing) return fields

  const nextQuantity = normalizeRepairAddonQuantity(quantity)
  const oldTotal = repairAddonLineTotal(existing)
  const nextAddon: AppliedEstimatedRepairAddon = {
    ...existing,
    quantity: nextQuantity,
  }
  const newTotal = repairAddonLineTotal(nextAddon)
  const delta = newTotal - oldTotal

  return adjustRepairCategoryAmount(
    {
      ...fields,
      estimated_repair_addons: currentAddons.map(addon =>
        addon.id === addonId ? nextAddon : addon,
      ),
    },
    existing.category,
    delta,
  )
}
